/* Attaches the pre-order disclosure properties to every add-to-cart path.
 *
 * The app's own widget JS would do this for the main product form only, and it
 * is not installed in this theme. A theme has more add-to-cart forms than the
 * PDP — quick-add on collection cards, search results, recommendation
 * carousels, sticky bars, upsell widgets, and bare fetch('/cart/add') calls —
 * and an order where only some lines carry the properties is worse than none.
 *
 * Rather than patching every form, this intercepts the request itself, so any
 * path that reaches /cart/add is covered including ones added later. It is
 * loaded from the cart drawer shell (snippets/cart-drawer) and the cart page
 * (sections/afroyla-main-cart), so it is present on every page — collection,
 * search, home, empty cart included — not only on product templates; the
 * drawer suggester and quick-add add to the cart from all of them.
 *
 * Properties written — the cross-store standard (Pre-order App,
 * docs/preorder-line-properties-standard.md, owner decision 2026-08-07).
 * Exactly these three, all hidden (underscore prefix), machine data for the
 * app's email flows, segments and coverage monitor:
 *
 *   _preorder        "true"           the flow / segment marker
 *   _preorder_batch  current.code     the works-order number, verbatim
 *   _preorder_eta    "Oct 20–Oct 27"  the window the shopper saw, canonical:
 *                                     short month + day, en dash, no spaces;
 *                                     one date when start == end; omitted
 *                                     entirely when the payload has no ETA
 *
 * Until 12.10.2026 this file wrote visible "Pre-order: Yes" / "Ships: …"
 * instead of _preorder / _preorder_eta. The cart snippets keep reading those
 * legacy keys as a fallback for lines added before the switch; nothing writes
 * them any more. The shopper still sees the promise: the theme renders it in
 * the cart UI from the hidden properties (afroyla-cart-line-card /
 * afroyla-cart-lines / afroyla-cart-totals).
 *
 * Server-side allocation does not depend on any of them.
 *
 * The registry comes from Liquid via afroyla-preorder-cart-data; a variant
 * absent from it is not on pre-order and its add is left untouched. */
(function () {
  'use strict';

  // The script is linked from the cart drawer AND from product-type sections;
  // wrapping fetch/XHR twice would be harmless but wasteful.
  if (window.__aflPreorderCartInit) return;
  window.__aflPreorderCartInit = true;

  var KEY = '_preorder';
  var KEY_ETA = '_preorder_eta';
  var KEY_BATCH = '_preorder_batch';
  var FLAG = 'true';

  /* The registry keeps the display form the rest of the theme prints
   * ("Oct 20 – Oct 27"). The property takes the standard's canonical form:
   * no spaces around the en dash, a single date when both ends are the same
   * day, and nothing at all when there is no window. */
  function canonicalEta(eta) {
    if (!eta) return '';
    var parts = String(eta).split(/\s*[–-]\s*/);
    if (parts.length === 2 && parts[0].trim() === parts[1].trim()) return parts[0].trim();
    return parts.map(function (p) { return p.trim(); }).filter(Boolean).join('–');
  }

  var registry = {};

  function loadRegistry(root) {
    var blobs = (root || document).querySelectorAll('script[data-afroyla-preorder]');
    for (var i = 0; i < blobs.length; i++) {
      try {
        var data = JSON.parse(blobs[i].textContent);
        for (var id in data) {
          if (Object.prototype.hasOwnProperty.call(data, id)) registry[id] = data[id];
        }
      } catch (e) {
        /* A malformed blob must not break adding to cart. */
      }
    }
  }

  function entry(variantId) {
    return variantId ? registry[String(variantId)] : null;
  }

  /* --- payload shapes /cart/add accepts ------------------------------- */

  function decorateFormData(fd) {
    var e = entry(fd.get('id'));
    if (!e || fd.get('properties[' + KEY + ']')) return fd;
    fd.append('properties[' + KEY + ']', FLAG);
    if (e.code) fd.append('properties[' + KEY_BATCH + ']', e.code);
    var fdEta = canonicalEta(e.eta);
    if (fdEta) fd.append('properties[' + KEY_ETA + ']', fdEta);
    return fd;
  }

  function decorateObject(obj) {
    // Single-line form: { id, quantity, properties }
    if (obj && obj.id && !obj.items) {
      var e = entry(obj.id);
      if (e) {
        obj.properties = obj.properties || {};
        if (!obj.properties[KEY]) {
          obj.properties[KEY] = FLAG;
          if (e.code) obj.properties[KEY_BATCH] = e.code;
          var objEta = canonicalEta(e.eta);
          if (objEta) obj.properties[KEY_ETA] = objEta;
        }
      }
    }
    // Multi-line form: { items: [...] }
    if (obj && Array.isArray(obj.items)) obj.items.forEach(decorateObject);
    return obj;
  }

  function isCartAdd(url) {
    if (!url) return false;
    return String(url).indexOf('/cart/add') !== -1;
  }

  /* --- fetch ---------------------------------------------------------- */

  var nativeFetch = window.fetch;
  if (nativeFetch) {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : input && input.url;
        if (isCartAdd(url) && init && init.body) {
          if (init.body instanceof FormData) {
            decorateFormData(init.body);
          } else if (typeof init.body === 'string') {
            // Only JSON bodies are safe to rewrite; a urlencoded body is left
            // alone rather than risking a malformed payload.
            try {
              init.body = JSON.stringify(decorateObject(JSON.parse(init.body)));
            } catch (e) {}
          }
        }
      } catch (e) {}
      return nativeFetch.apply(this, arguments);
    };
  }

  /* --- XMLHttpRequest -------------------------------------------------- */

  var open = XMLHttpRequest.prototype.open;
  var send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__afroylaCartAdd = isCartAdd(url);
    return open.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    try {
      if (this.__afroylaCartAdd && body) {
        if (body instanceof FormData) {
          decorateFormData(body);
        } else if (typeof body === 'string') {
          try {
            arguments[0] = JSON.stringify(decorateObject(JSON.parse(body)));
          } catch (e) {}
        }
      }
    } catch (e) {}
    return send.apply(this, arguments);
  };

  /* --- native form posts ---------------------------------------------- */

  function hidden(form, name, value) {
    var input = form.querySelector('input[name="properties[' + name + ']"][data-afroyla-po]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + name + ']';
      input.setAttribute('data-afroyla-po', '');
      form.appendChild(input);
    }
    input.value = value;
  }

  function clear(form) {
    var stale = form.querySelectorAll('input[data-afroyla-po]');
    for (var i = 0; i < stale.length; i++) stale[i].remove();
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.action || !isCartAdd(form.action)) return;
      clear(form);
      var idField = form.querySelector('[name="id"]');
      var e = entry(idField && idField.value);
      if (!e) return;
      hidden(form, KEY, FLAG);
      if (e.code) hidden(form, KEY_BATCH, e.code);
      var formEta = canonicalEta(e.eta);
      if (formEta) hidden(form, KEY_ETA, formEta);
    },
    true // capture, so the values exist before any other submit handler reads the form
  );

  /* --- registry lifecycle --------------------------------------------- */

  loadRegistry(document);

  // Quick-add modals and recommendation carousels inject markup after load.
  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('script[data-afroyla-preorder]')) {
            loadRegistry(node.parentNode);
          } else if (node.querySelector && node.querySelector('script[data-afroyla-preorder]')) {
            loadRegistry(node);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
